import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOrder, getOrderVendorsStatus, matchVendor } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Search, 
  Minus, 
  Plus, 
  Loader2, 
  Truck, 
  PackageCheck,
  CheckCircle,
  MessageSquare,
  ChevronLeft,
  ChevronDown,
  ChevronUp
} from "lucide-react";

export default function MatchOrderPage() {
  const { orderId, vendorName } = useParams();
  const navigate = useNavigate();
  const decodedVendorName = decodeURIComponent(vendorName);
  
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Items for this vendor
  const [matchItems, setMatchItems] = useState([]);
  
  // Invoice details
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [notes, setNotes] = useState("");
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [orderId, vendorName]);

  const loadData = async () => {
    try {
      const orderRes = await getOrder(orderId);
      setOrder(orderRes.data);
      
      // Filter items for this vendor and initialize match quantities
      const vendorItems = orderRes.data.items
        .filter(item => item.vendor_name === decodedVendorName)
        .map(item => ({
          ...item,
          quantity_received: item.quantity_received ?? item.quantity_to_order,
          receive_notes: item.receive_notes || ""
        }))
        .sort((a, b) => a.item_name.localeCompare(b.item_name));
      
      setMatchItems(vendorItems);
    } catch (error) {
      toast.error("Failed to load order");
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search
  const handleSearchInput = (e) => {
    const value = e.target.value;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 150);
  };

  // Filter items
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return matchItems;
    const search = searchQuery.toLowerCase();
    return matchItems.filter(item => 
      item.item_name.toLowerCase().includes(search)
    );
  }, [matchItems, searchQuery]);

  // Update item
  const updateItem = (itemId, field, value) => {
    setMatchItems(prev => prev.map(item => 
      item.item_id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Adjust quantity
  const adjustQuantity = (itemId, delta) => {
    setMatchItems(prev => prev.map(item => {
      if (item.item_id === itemId) {
        const newQty = Math.max(0, (item.quantity_received || 0) + delta);
        return { ...item, quantity_received: newQty };
      }
      return item;
    }));
  };

  // Submit
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const data = {
        vendor_name: decodedVendorName,
        items: matchItems.map(item => ({
          item_id: item.item_id,
          quantity_received: item.quantity_received || 0,
          notes: item.receive_notes || null
        })),
        invoice_number: invoiceNumber || null,
        invoice_date: invoiceDate || null,
        notes: notes || null
      };
      
      await matchVendor(orderId, data);
      toast.success(`${decodedVendorName} delivery received!`);
      navigate(`/orders/${orderId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout title="Receive Delivery" showBack>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Receive Delivery" showBack>
      <div className="p-4 md:p-6 pb-32 max-w-2xl mx-auto">
        {/* Vendor Header */}
        <Card className="mb-4 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-700" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-blue-900">{decodedVendorName}</h2>
                <p className="text-sm text-blue-700">
                  {matchItems.length} items to receive
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Details */}
        <Card className="mb-4">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-medium text-stone-700 text-sm">Invoice Details (Optional)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-500">Invoice #</Label>
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="INV-001"
                  className="mt-1 h-10"
                />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Date</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="mt-1 h-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="sticky top-0 z-20 bg-bone pt-1 pb-3 -mx-4 px-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <input
              ref={searchInputRef}
              type="search"
              placeholder="Search items..."
              defaultValue=""
              onInput={handleSearchInput}
              className="flex h-11 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 pl-10 text-base ring-offset-white placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kale-500 focus-visible:ring-offset-2"
            />
          </div>
        </div>

        {/* Items */}
        <div className="space-y-3">
          {filteredItems.map((item) => (
            <Card key={item.item_id} className="card-hover" data-testid={`match-item-${item.item_id}`}>
              <CardContent className="p-4 space-y-3">
                {/* Item Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-stone-900">{item.item_name}</h3>
                    <p className="text-sm text-stone-500">
                      {item.unit}{item.pack_size ? ` • ${item.pack_size}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-stone-400">Ordered</p>
                    <p className="font-mono font-semibold text-stone-700">{item.quantity_to_order}</p>
                  </div>
                </div>

                {/* Received Quantity */}
                <div>
                  <Label className="text-xs text-stone-500 mb-1 block">Received Quantity</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-11 w-11"
                      onClick={() => adjustQuantity(item.item_id, -1)}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      min="0"
                      value={item.quantity_received || 0}
                      onChange={(e) => updateItem(item.item_id, "quantity_received", Math.max(0, parseFloat(e.target.value) || 0))}
                      className="h-11 text-center font-mono flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-11 w-11"
                      onClick={() => adjustQuantity(item.item_id, 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    {item.quantity_received !== item.quantity_to_order && (
                      <Badge 
                        variant="secondary" 
                        className={`${item.quantity_received < item.quantity_to_order ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'} font-mono`}
                      >
                        {item.quantity_received > item.quantity_to_order ? '+' : ''}{item.quantity_received - item.quantity_to_order}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-stone-400" />
                  <Textarea
                    placeholder="Notes (optional)"
                    value={item.receive_notes || ""}
                    onChange={(e) => updateItem(item.item_id, "receive_notes", e.target.value)}
                    className="pl-10 min-h-[44px] resize-none text-base"
                    rows={1}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* General Notes */}
        <Card className="mt-4">
          <CardContent className="p-4">
            <Label className="text-xs text-stone-500 mb-2 block">Delivery Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any issues with this delivery..."
              className="min-h-[60px]"
            />
          </CardContent>
        </Card>
      </div>

      {/* Submit Button */}
      <div className="sticky-bottom">
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full btn-accent"
          data-testid="confirm-receive-button"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <PackageCheck className="w-4 h-4 mr-2" />
              Confirm Received ({matchItems.length} items)
            </>
          )}
        </Button>
      </div>
    </AppLayout>
  );
}
